'use client'

import { useEffect, useRef, useState } from 'react'
import type { AnimationItem } from 'lottie-web'
import clsx from 'clsx'

export type LottieIconProps = {
  src: string // e.g. "/Lottie/Halloween2025tag.json" under public
  width?: number | string
  height?: number | string
  preserveAspectRatio?: string
  freezeOnComplete?: boolean
  loop?: boolean
  autoplay?: boolean
  speed?: number
  className?: string
  style?: React.CSSProperties
  hoverPlay?: boolean // play on hover; useful for non-autoplay icons
  onComplete?: () => void
  play?: boolean
  freezeFrame?: 'first' | 'last' | null
  restartToken?: number | string
}

export function LottieIcon({
  src,
  width = 120,
  height = 120,
  preserveAspectRatio = 'xMidYMid slice',
  freezeOnComplete = false,
  loop = true,
  autoplay = true,
  speed = 1,
  className,
  style,
  hoverPlay = false,
  onComplete,
  play,
  freezeFrame = null,
  restartToken,
}: LottieIconProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const animationRef = useRef<AnimationItem | null>(null)
  const onCompleteRef = useRef(onComplete)
  const freezeOnCompleteRef = useRef(freezeOnComplete)
  const previousPlayRef = useRef(false)
  const previousRestartTokenRef = useRef<number | string | null>(null)
  const [isDomLoaded, setIsDomLoaded] = useState(false)

  const stopAtFrame = (
    anim: AnimationItem,
    frame: 'first' | 'last' | null | undefined
  ) => {
    if (frame === 'last') {
      anim.goToAndStop(Math.max(anim.totalFrames - 1, 0), true)
      return
    }

    anim.goToAndStop(0, true)
  }

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    freezeOnCompleteRef.current = freezeOnComplete
  }, [freezeOnComplete])

  useEffect(() => {
    if (!containerRef.current) return

    setIsDomLoaded(false)
    previousPlayRef.current = false
    previousRestartTokenRef.current = null

    let cancelled = false
    let anim: AnimationItem | null = null
    const handleDomLoaded = () => {
      setIsDomLoaded(true)
    }
    const handleComplete = () => {
      if (anim && freezeOnCompleteRef.current) {
        stopAtFrame(anim, 'last')
      }

      onCompleteRef.current?.()
    }

    // 动态加载 lottie-web 运行时，避免 ~300KB 进入引用页面的首屏 chunk
    import('lottie-web').then(({ default: lottie }) => {
      if (cancelled || !containerRef.current) return

      anim = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: src,
        rendererSettings: {
          preserveAspectRatio,
          progressiveLoad: true,
          hideOnTransparent: true,
        },
      })

      animationRef.current = anim
      anim.addEventListener('DOMLoaded', handleDomLoaded)
      anim.addEventListener('complete', handleComplete)
    })

    return () => {
      cancelled = true
      if (anim) {
        anim.removeEventListener('DOMLoaded', handleDomLoaded)
        anim.removeEventListener('complete', handleComplete)
        anim.destroy()
      }
      animationRef.current = null
    }
  }, [src, preserveAspectRatio])

  useEffect(() => {
    if (!animationRef.current) return
    animationRef.current.loop = loop
  }, [loop, isDomLoaded])

  useEffect(() => {
    if (!animationRef.current) return
    animationRef.current.setSpeed(speed)
  }, [speed, isDomLoaded])

  useEffect(() => {
    const anim = animationRef.current
    if (!anim || !isDomLoaded || hoverPlay) return

    const shouldPlay = play ?? autoplay
    const normalizedRestartToken =
      restartToken === undefined ? null : restartToken
    const restartChanged =
      normalizedRestartToken !== previousRestartTokenRef.current
    const shouldRestart =
      shouldPlay && (restartChanged || !previousPlayRef.current)

    if (shouldRestart) {
      anim.goToAndPlay(0, true)
    } else if (!shouldPlay) {
      if (freezeFrame) {
        stopAtFrame(anim, freezeFrame)
      } else {
        anim.stop()
      }
    }

    previousPlayRef.current = shouldPlay
    previousRestartTokenRef.current = normalizedRestartToken
  }, [autoplay, freezeFrame, hoverPlay, isDomLoaded, play, restartToken])

  const handleMouseEnter = () => {
    if (hoverPlay) animationRef.current?.play()
  }

  const handleMouseLeave = () => {
    if (hoverPlay) animationRef.current?.stop()
  }

  return (
    <div
      ref={containerRef}
      className={clsx('inline-block', className)}
      style={{ width, height, ...style }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-hidden
    />
  )
}
